import * as deepEqual from 'deep-equal';
import * as uuid from 'uuid';

import { diacriticsMap } from './diacritics';
import { DataUrl } from './models/inline-resources.model';

// TODO @todo "fromEntries()" might be obsolete as soon as es2019 is included as lib in tsconfig.json
// See https://www.reddit.com/r/typescript/comments/e8abzr/how_can_i_make_objectfromentries_and/
// and https://github.com/microsoft/TypeScript/issues/30933#issuecomment-586735655
declare global {
  interface ObjectConstructor {
    // /**
    //  * Returns an object created by key-value entries for properties and methods
    //  * @param entries An iterable object that contains key-value entries for properties and methods.
    //  */
    // fromEntries<T = any>(entries: Iterable<readonly [PropertyKey, T]>): { [k: string]: T };

    /**
     * Returns an object created by key-value entries for properties and methods
     * @param entries An iterable object that contains key-value entries for properties and methods.
     */
    fromEntries(entries: Iterable<readonly any[]>): any;
  }
}

const fromEntries = (entries: Iterable<readonly any[]>) =>
  Object.fromEntries
    ? Object.fromEntries(entries)
    : Array.from(entries).reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

export class CollectionHelper {
  public static safePushValueToArray<T>(arr: T[], val: T) {
    if (!CompareHelper.isNullorEmpty(val)) {
      arr.push(val);
    }
  }

  public static insertAt<T>(array: T[], index: number, ...elementsArray: T[]): void {
    array.splice(index, 0, ...elementsArray);
  }

  public static removeAt<T>(array: T[], index: number): void {
    array.splice(index, 1);
  }

  public static removeItem<T>(array: T[], itemToRemove: T): boolean {
    const index = array?.indexOf(itemToRemove);
    if (index >= 0) {
      this.removeAt(array, index);
      return true;
    }
    return false;
  }

  public static removeItems<T>(array: T[], itemsToRemove: T[]): number {
    if (itemsToRemove) {
      let result = 0;
      for (const item of itemsToRemove) {
        if (this.removeItem(array, item)) {
          result++;
        }
      }
      return result;
    }
    return 0;
  }

  public static removeByFilter<T>(array: T[], predicate: (value: T, index: number, array: T[]) => unknown): number {
    return this.removeItems(array, predicate ? array?.filter(predicate) : undefined);
  }

  public static flatten<T>(arrayOfArrays: T[][]): T[] {
    return arrayOfArrays.reduce((flattened, toFlatten) => flattened.concat(toFlatten), <T[]>[]);
  }

  public static groupBy<T, K>(array: T[], getKey: (item: T) => K): Grouping<K, T>[] {
    const map = new Map<K, T[]>();

    if (array) {
      array.forEach((item) => {
        const key = getKey(item);
        const collection = map.get(key);
        if (!collection) {
          map.set(key, [item]);
        } else {
          collection.push(item);
        }
      });
    }

    const result: Grouping<K, T>[] = [];
    map.forEach((v, k) => result.push(new Grouping(k, v)));

    return result;
  }

  public static removeDuplicates<T>(array: T[], equalsPredicate?: (val1: T, val2: T) => boolean): T[] {
    if (equalsPredicate) {
      return (array ?? []).filter(
        (item1, index1, arr1) => arr1.findIndex((item2, index2, arr2) => equalsPredicate(item1, item2)) === index1,
      );
    } else {
      return (array ?? []).filter((item, index, arr) => arr.indexOf(item) === index);
    }
  }

  public static distinctValues<T>(input: T[]): T[] {
    return this.distinct(input, (_) => _);
  }

  public static distinct<T, TKey>(input: T[], getKey?: (item: T) => TKey): T[] {
    return this.removeDuplicates(input, getKey ? (i1, i2) => getKey(i1) === getKey(i2) : undefined);
  }

  public static orderBy<T>(input: T[], ...sortValues: ((item: T) => unknown)[]): T[] {
    return this.orderByCore(input, sortValues, (a, b) => CompareHelper.compare(a, b));
  }

  public static orderByDescending<T, TKey>(input: T[], ...sortValues: ((item: T) => unknown)[]): T[] {
    return this.orderByCore(input, sortValues, (a, b) => -CompareHelper.compare(a, b));
  }

  private static orderByCore<T>(
    input: T[],
    sortValues: ((item: T) => unknown)[],
    compareFn: (a: unknown, b: unknown) => number,
  ): T[] {
    for (let i = sortValues.length - 1; i >= 0; i--) {
      const sortValue = sortValues[i];
      input.sort((a, b) => compareFn(sortValue(a), sortValue(b)));
    }
    return input;
  }
}

export class Grouping<K, T> extends Array<T> {
  public readonly key: K;

  public constructor(key: K, items: T[]) {
    super(...items);
    this.key = key;
  }
}

export class CompareHelper {
  public static compare(value1: any, value2: any): number {
    if (value1 < value2) {
      return -1;
    } else if (value1 > value2) {
      return 1;
    } else {
      return 0;
    }
  }

  public static isEmptyObject(obj: unknown): boolean {
    return (
      !obj ||
      (Object.entries(obj).length === 0 && obj.constructor === Object) ||
      (Array.isArray(obj) && obj.length === 0)
    );
  }

  public static isNullorEmpty(val: unknown): boolean {
    return !val && typeof val !== 'number' && typeof val !== 'boolean';
  }

  public static isNullorFalse(val: unknown): boolean {
    if (val === false) {
      return true;
    }
    return !val && typeof val !== 'number' && typeof val !== 'boolean';
  }

  public static areDeepEqual(obj1: any, obj2: any, strict?: boolean): boolean {
    return deepEqual(obj1, obj2, strict ? { strict: strict } : null);
  }
}

export class ConvertHelper {
  public static toBoolean(value: any): boolean {
    const valueText: string = ConvertHelper.toString(value);
    return valueText?.toLowerCase() === 'true' || valueText?.toLowerCase() === '1';
  }

  public static fromString(
    destType: new () => String | Number | Boolean | Date | Object | [],
    text: string,
  ): string | number | boolean | Date | Object | [] {
    let value: string | number | boolean | Date | Object | [] = null;

    if (destType && text) {
      const templateObj: String | Number | Boolean | Date | Object | [] = new destType(); // is always object

      if (!text) {
        value = null;
      } else if (templateObj instanceof Date) {
        value = new Date(text.valueOf());
      } else if (templateObj instanceof String) {
        value = text.valueOf();
      } else if (templateObj instanceof Number) {
        value = Number(text.valueOf()).valueOf();
      } else if (templateObj instanceof Boolean) {
        value = text.toLowerCase() === 'true' || text.toLowerCase() === '1' ? true : false;
      } else if (Array.isArray(templateObj) || templateObj instanceof Object) {
        value = JSON.parse(text);
      } else {
        throw new Error(`Unsupported destination type ${destType.name}.`);
      }
    }

    return value;
  }

  public static toString(value: any, spaceIfJSON?: string | number): string {
    let valueString: string = undefined;

    if (CompareHelper.isNullorEmpty(value)) {
      valueString = '';
    } else {
      if (typeof value === 'object') {
        if (value instanceof Date) {
          valueString = value.toISOString();
        } else if (Array.isArray(value)) {
          valueString = `[ ${value.map((i) => ConvertHelper.toString(i, spaceIfJSON)).join(', ')} ]`;
        } else {
          // if (String(value)?.startsWith('[object ')) {
          try {
            valueString = JSON.stringify(value, null, spaceIfJSON);
          } catch {}
        }
      }

      if (valueString === undefined) {
        valueString = String(value);
      }
    }

    return valueString;
  }

  public static toLocaleFormattedDateOrTimeString(value: Date | string): string {
    if (CompareHelper.isNullorEmpty(value)) {
      return undefined;
    }
    if (typeof value === 'string') {
      value = new Date(value);
    }
    const locale = document.documentElement.lang ? document.documentElement.lang : 'de';
    const isTime: boolean =
      value.getHours() > 0 || value.getMinutes() > 0 || value.getSeconds() > 0 || value.getMilliseconds() > 0;
    if (isTime) {
      const options: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
      };
      return value.toLocaleTimeString(locale, options);
    } else {
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      };
      return value.toLocaleDateString(locale, options);
    }
  }

  public static toLocaleFormattedDateString(
    value: string | Date | number,
    showWeekday?: boolean,
    options?: Intl.DateTimeFormatOptions,
  ): string {
    const lang = sessionStorage.getItem('currentUserLanguage') ?? document.documentElement.lang;

    if (CompareHelper.isNullorEmpty(value)) {
      return undefined;
    }
    const date = new Date(value.toLocaleString());

    options = options ?? {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };

    if (showWeekday) {
      options.weekday = 'short';
    }
    return date.toLocaleDateString(lang ?? 'de', options);
  }

  public static toLocaleFormattedDateTimeString(value: number | Date | string, options?: Intl.DateTimeFormatOptions) {
    if (!options) {
      options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      };
    }
    const language = document.documentElement.lang ? document.documentElement.lang : 'de';
    return new Date(value).toLocaleDateString(language, options);
  }

  public static toTime(value: string, withSeconds?: boolean): string {
    if (CompareHelper.isNullorEmpty(value)) {
      return undefined;
    }
    const date = new Date(value);
    const locale = document.documentElement.lang ? document.documentElement.lang : 'de';
    const time: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
    };

    if (withSeconds) {
      time.second = '2-digit';
    }

    return date.toLocaleTimeString(locale, time);
  }

  /**
   * e.g. to persist date in WebApi.
   */
  public static toISODateString(date: Date): string {
    if (date) {
      let d = new Date(date);
      let month: string = '' + (d.getMonth() + 1);
      let day: string = '' + d.getDate();
      let year: string = '' + d.getFullYear();

      if (month.length < 2) month = '0' + month;
      if (day.length < 2) day = '0' + day;

      return [year, month, day].join('-');
    } else {
      return null;
    }
  }

  /**
   * e.g. to persist time in WebApi
   */
  public static toISOTimeString(date: Date): string {
    if (date) {
      let d = new Date(date);

      let hours: string = '' + d.getHours();
      let minutes: string = '' + d.getMinutes();
      let seconds: string = '' + d.getSeconds();

      if (hours.length < 2) hours = '0' + hours;
      if (minutes.length < 2) minutes = '0' + minutes;
      if (seconds.length < 2) seconds = '0' + seconds;

      return [hours, minutes, seconds].join(':');
    } else {
      return null;
    }
  }

  public static toDate(str: string): Date {
    const locale = sessionStorage.getItem('currentUserLanguage') ?? document.documentElement.lang ?? 'de';

    let day: number;
    let month: number;
    let year: number;

    if (!str) {
      return undefined;
    }

    const delimiters = ['.', '-', '/', '\\', ' '];
    const delimiter = delimiters.find((s) => str.includes(s));
    const reversed = str.split(delimiter)[0].length > 2;

    if (reversed) {
      [year, month, day] = str.split(delimiter).map(Number);
    } else if (locale) {
      switch (locale) {
        case 'en':
          [month, day, year] = str.split(delimiter).map(Number);
          break;
        case 'fr':
        case 'de':
          [day, month, year] = str.split(delimiter).map(Number);
          break;
        default:
          [day, month, year] = str.split(delimiter).map(Number);
          break;
      }
    } else {
      [day, month, year] = str.split(delimiter).map(Number);
    }

    return new Date(year, month - 1, day);
  }

  public static convertObject<T = any, U = any>(objSource: T): U {
    return objSource ? Object.fromEntries(Object.entries(objSource)) : null;
  }

  public static getEnumEntries<
    V extends number | string = number | string,
    T extends Record<Extract<keyof T, string>, number | string> = any,
  >(enumType: T): { Key: string; Value: V }[] {
    return Object.entries(enumType)
      .filter((e) => isNaN(parseInt(e[0])))
      .map((e) => ({ Key: e[0], Value: e[1] as V }));
  }

  public static getEnumKeyByEnumValue<TEnumKey extends string, TEnumVal extends string | number>(
    targetEnum: { [key in TEnumKey]: TEnumVal },
    enumValue: TEnumVal,
  ): string {
    const keys = (Object.keys(targetEnum) as TEnumKey[]).filter((x) => targetEnum[x] === enumValue);
    return keys.length > 0 ? keys[0] : '';
  }

  /**
   * @param  value The initial value;
   * @value  value The initial value;
   * @param characterToReplace The special character that will be replaced
   * @characterToReplace ' ' , ',' , '-' ...
   * @param specialCharacter The special character that will replace
   * @specialCharacter ' ' , ',' , '-', '_' ...
   * @param replaceDiacritics if true the function will remove german/french or other diacritics.
   * @replaceDiacritics ä ö ü ...
   * @returns The formatted value as string
   */
  public static stringReplaceSpecialCharacters(
    value: string,
    characterToReplace: string,
    specialCharacter: string,
    replaceDiacritics?: boolean,
  ): string {
    if (replaceDiacritics) {
      for (let i = 0; i < diacriticsMap.length; i++) {
        value = value?.replace(diacriticsMap[i].letters, diacriticsMap[i].base);
      }
    }
    value = value?.replace(new RegExp(characterToReplace, 'g'), specialCharacter);
    // value = value.replaceAll(characterToReplace, specialCharacter,);
    return value;
  }

  /**
   * Format bytes as human-readable text.
   * @param bytes Number of bytes.
   * @param si True to use metric (SI) units, aka powers of 1000. False to use
   *           binary (IEC), aka powers of 1024.
   * @param dp Number of decimal places to display.
   * @return Formatted string.
   */
  public static toHumanByteString(bytes, si = false, dp = 1): string {
    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
      return bytes + ' B';
    }

    const units = si
      ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
      : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    const r = 10 ** dp;

    do {
      bytes /= thresh;
      ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

    return bytes.toFixed(dp) + ' ' + units[u];
  }

  public static uint8ArrayToArrayBuffer(array: Uint8Array): ArrayBuffer {
    return <ArrayBuffer>array.buffer.slice(array.byteOffset, array.byteLength + array.byteOffset);
  }

  public static camelize(str: string): string {
    return str
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
      })
      .replace(/\s+/g, '');
  }

  public static arrayBufferToObject<T>(arrayBuffer: ArrayBuffer): T | undefined {
    const arr = this.arrayBufferToObjectArray<T>(arrayBuffer);
    if (arr?.length) {
      return arr[0];
    }
    return !CompareHelper.isEmptyObject(arrayBuffer) && ObjectHelper.isObject(arrayBuffer)
      ? (arrayBuffer as unknown as T)
      : undefined;
  }

  public static arrayBufferToObjectArray<T>(results: ArrayBuffer): T[] {
    if (results && Array.isArray(results)) {
      return results;
    } else {
      return [];
    }
  }

  /**
   * Takes a base64 string and convert it to a file
   * @param base64String
   * @param fileName
   * @param mimeType
   * @returns
   */
  public static base64ToFile = (base64String: string, fileName: string, mimeType: string): File => {
    const byteCharacters = atob(base64String.split(',')[1]); // Remove "data:mime/type;base64,"
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    return new File([blob], fileName, { type: mimeType });
  };

  /**
   * Takes a file and convert it to a base64 string
   * BEC: I would assume that this isn't that what you want. Use readFileAsDataUrl() instead.
   * @param file type File
   * @returns
   */
  public static fileToBase64(file: File, includeMimetype = true): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(includeMimetype ? <string>reader.result : (<string>reader.result).split(',')[1]);
      reader.onerror = (error) => reject(error);
    });
  }

  public static readFileAsDataUrl(file: File): Promise<DataUrl> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(DataUrl.fromString(reader.result as string));
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(file);
    });
  }
}

export class GUID {
  public static newGUID() {
    const id = uuid.v4();
    return id;
  }
  public static isGUID(value: string): boolean {
    return value?.length === 36 && value[8] === '-' && value[13] === '-' && value[18] === '-' && value[23] === '-';
  }
}

type Constructor<T> = new (...args: any[]) => T;

export class ObjectHelper {
  public static loadObject(targetDefinition: any, data: any): any {
    if (!targetDefinition) {
      console.log(data);
      return data;
    }
    let targetType = targetDefinition.prototype;
    let c = targetType.constructor;
    if (c.length === 1) {
      return c(data);
    }
    let target = new c();
    let dict = targetDefinition.__DataObjectHelperCache__;
    if (!dict) {
      dict = {};

      for (let prop in target) {
        let definition = targetDefinition[prop + '__'];
        dict[prop] = {
          name: prop,
          definition: definition,
        };
      }

      targetDefinition.__DataObjectHelperCache__ = dict;
    }

    for (let prop in data) {
      let targetprop = dict[prop];
      if (!targetprop) {
        let targetnameLower = prop.toLowerCase();
        for (let cached in dict) {
          if (cached.toLowerCase() === targetnameLower) {
            targetprop = dict[cached];
            dict[prop] = targetprop;
            break;
          }
        }
      }
      let value = data[prop];
      let definition: any = targetprop.definition;
      if (typeof value === 'object') {
        if (Array.isArray(value)) {
          let arr = [];
          for (let i = 0; i < value.length; i++) {
            let item = value[i];
            if (definition && item) {
              item = this.loadObject(definition, item);
            }
            arr.push(item);
          }
          target[targetprop.name] = arr;
        } else {
          if (definition) {
            value = this.loadObject(definition, value);
          } else {
            value = Object.assign({}, value);
          }
          target[targetprop.name] = value;
        }
      } else {
        if (definition && value) {
          value = this.loadObject(definition, value);
        }
        target[targetprop.name] = value;
      }
    }
    return target;
  }

  /**
   * Simple object check.
   * @param item
   * @returns {boolean}
   */
  public static isArray(item: unknown): boolean {
    return Array.isArray(item);
  }

  public static asArray<T>(object: T[] | Iterable<T> | ArrayLike<T>): T[] {
    if (!object) {
      return [];
    } else if (Array.isArray(object)) {
      return <T[]>object;
    } else {
      return Array.from(object);
    }
  }

  /**
   * Simple object check.
   * @param item
   * @returns {boolean}
   */
  public static isObject(item: unknown): boolean {
    return this.isObjectCore(item) && !this.isArray(item);
  }

  private static isObjectCore(item: unknown): boolean {
    return item && typeof item === 'object';
  }

  /**
   * Simple object check.
   * @param item
   * @returns {boolean}
   */
  public static isObjectOrArray(item: unknown): boolean {
    return this.isObjectCore(item) || this.isArray(item);
  }

  /**
   * Merge two objects.
   * @param target
   * @param ...sources
   */
  public static merge<T>(target: T, ...sources: T[]): T {
    const copy: T = this.clone(target);
    this.mergeInto(copy, ...sources);
    return copy;
  }

  /**
   * Merge two objects.
   * @param target
   * @param ...sources
   */
  public static mergeInto<T>(target: T, ...sources: T[]): void {
    if (sources?.length && this.isObject(target)) {
      const t: any = target;
      for (const source of sources) {
        if (this.isObject(source)) {
          for (const key in source) {
            const src = source[key];
            if (src !== undefined) {
              t[key] = src;
            }
          }
        }
      }
    }
  }

  /**
   * Deep merge two objects.
   * @param target
   * @param ...sources
   */
  public static mergeDeep<T>(target: T, ...sources: T[]): T {
    const copy = this.cloneDeep(target);
    this.mergeDeepInto(copy, ...sources);
    return copy;
  }

  /**
   * Deep merge two objects.
   * @param target
   * @param ...sources
   */
  public static mergeDeepInto<T>(target: T, ...sources: T[]): void {
    if (sources?.length && this.isObject(target)) {
      const t: any = target;
      for (const source of sources) {
        if (this.isObject(source)) {
          for (const key in source) {
            const src = source[key];
            if (this.isArray(src)) {
              t[key] = this.cloneArray(<unknown[]>(<unknown>src));
            } else if (this.isObjectCore(src)) {
              const dest = target[key];
              if (this.isObject(dest)) {
                this.mergeDeepInto(dest, src);
              } else {
                t[key] = this.cloneDeep(src);
              }
            } else if (src !== undefined) {
              t[key] = src;
            }
          }
        }
      }
    }
  }

  public static clone<T>(source: T): T {
    if (Array.isArray(source)) {
      return <any>[...source];
    } else if (this.isObjectCore(source)) {
      const clone: any = {};
      for (const key in source) {
        clone[key] = source[key];
      }
      return clone;
    } else {
      return source;
    }
  }

  public static cloneDeep<T>(source: T): T {
    if (this.isArray(source)) {
      return this.cloneArrayCore(source);
    } else if (this.isObjectCore(source)) {
      const clone: any = {};
      for (const key in source) {
        clone[key] = this.cloneDeep(source[key]); // recursively unlink reference to nested objects.
      }
      return clone;
    } else {
      return source;
    }
  }

  public static replaceValuesDeep(target: any, sourceValue: unknown, replaceValue: unknown): boolean {
    const result = this.tryReplaceValueCore(target, sourceValue, replaceValue);
    return result.replaced;
  }

  static tryReplaceValueCore(
    target: any,
    sourceValue: unknown,
    replaceValue: unknown,
  ): { replaced: boolean; needsNewValue: boolean; newValue: any } {
    let replaced: boolean = false;
    let needsNewValue: boolean = false;
    let newValue: any = target;
    if (CompareHelper.areDeepEqual(target, sourceValue, true)) {
      newValue = replaceValue;
      needsNewValue = true;
      replaced = true;
    } else if (this.isArray(target)) {
      for (let i = 0; i < target.length; i++) {
        const v = target[i];
        const result = this.tryReplaceValueCore(v, sourceValue, replaceValue);
        if (result.replaced) {
          if (result.needsNewValue) {
            target[i] = result.newValue;
          }
          replaced = true;
        }
      }
    } else if (this.isObjectCore(target)) {
      for (const key in target) {
        const v = target[key];
        const result = this.tryReplaceValueCore(v, sourceValue, replaceValue);
        if (result.replaced) {
          if (result.needsNewValue) {
            target[key] = result.newValue;
          }
          replaced = true;
        }
      }
    }
    return { replaced: replaced, needsNewValue: needsNewValue, newValue: newValue };
  }

  public static cloneArray<T>(source: T[]): T[] {
    return this.cloneArrayCore(source);
  }

  static cloneArrayCore(source: any): any {
    if (source) {
      const arr: any[] = [];
      for (const item of source) {
        arr.push(this.cloneDeep(item));
      }
      return arr;
    }
    return source;
  }

  public static nameof<T>(key: keyof T, instance?: T): keyof T {
    return key;
  }

  public static isOfType<T>(obj: object, filterType: Constructor<T>): boolean {
    return obj instanceof filterType;
  }

  public static implementsInterface<T>(obj: unknown, instance: T): obj is T {
    if (!obj || !instance) {
      return false;
    }
    const o = <any>obj;
    const implementedKeys = Object.keys(obj);
    for (const key in instance) {
      if (!implementedKeys.includes(key) && o[key] === undefined) {
        return false;
      }
    }
    return true;
  }

  public static cleanupUndefinedValues(obj: object) {
    if (obj) {
      for (const key in obj) {
        const v = obj[key];
        if (v === undefined) {
          delete obj[key];
        } else if (this.isObjectOrArray(v)) {
          this.cleanupUndefinedValues(v);
        }
      }
    }
  }

  public static groupByListItems(list: any[], groupByKey: string, key: string) {
    return list.reduce((arr, obj) => {
      (arr[obj[groupByKey]] = arr[obj[groupByKey]] || []).push(obj[key]);
      return arr;
    }, {});
  }
}

export class StringHelper {
  public static extractNumber(value: string): number {
    const match = value.match(/\d/g);
    const numb: number = parseInt(match?.join(''));
    return numb;
  }

  public static trim(value: string, ...trimChars: string[]) {
    return this.trimCore(value, trimChars, true, true);
  }

  public static trimStart(value: string, ...trimChars: string[]) {
    return this.trimCore(value, trimChars, true, false);
  }

  public static trimEnd(value: string, ...trimChars: string[]) {
    return this.trimCore(value, trimChars, false, true);
  }

  private static trimCore(value: string, trimChars: string[], start: boolean, end: boolean) {
    if (!trimChars?.length) {
      trimChars = [' ', '\n', '\r', '\t'];
    }
    if (start) {
      let modified: boolean;
      do {
        modified = false;
        for (const c of trimChars) {
          while (value.startsWith(c)) {
            value = value.substring(c.length);
            modified = true;
          }
        }
      } while (modified);
    }
    if (end) {
      let modified: boolean;
      do {
        modified = false;
        for (const c of trimChars) {
          while (value.endsWith(c)) {
            value = value.substring(0, value.length - c.length);
            modified = true;
          }
        }
      } while (modified);
    }
    return value;
  }

  private static charCodeZero = '0'.charCodeAt(0);
  private static charCodeNine = '9'.charCodeAt(0);
  private static charCodeUpperA = 'A'.charCodeAt(0);
  private static charCodeUpperZ = 'Z'.charCodeAt(0);
  private static charCodeLowerA = 'a'.charCodeAt(0);
  private static charCodeLowerZ = 'z'.charCodeAt(0);

  public static isDigit(char: string, index: number = 0) {
    if (index < char?.length) {
      const code = char.charCodeAt(index);
      return code >= this.charCodeZero && code <= this.charCodeNine;
    }
    return false; // Explicitly return false if index is out of bounds
  }

  public static isLetter(char: string, index: number = 0) {
    if (index < char?.length) {
      const code = char.charCodeAt(index);
      return (
        (code >= this.charCodeUpperA && code <= this.charCodeUpperZ) ||
        (code >= this.charCodeLowerA && code <= this.charCodeLowerZ)
      );
    }
    return false; // Explicitly return false if index is out of bounds
  }

  public static isLetterOrDigit(char: string, index: number = 0) {
    if (index < char?.length) {
      const code = char.charCodeAt(index);
      return (
        (code >= this.charCodeZero && code <= this.charCodeNine) ||
        (code >= this.charCodeUpperA && code <= this.charCodeUpperZ) ||
        (code >= this.charCodeLowerA && code <= this.charCodeLowerZ)
      );
    }
    return false; // Explicitly return false if index is out of bounds
  }
}

export function overrideDefaultRootColors(config) {
  const headTag = document.getElementsByTagName('head')[0];
  const styleTag = document.createElement('style');
  let root: string;
  const style = config?.Style?.Colors;
  if (!CompareHelper.isEmptyObject(style)) {
    root = ':root {';
    for (const key in style) {
      if (Object.prototype.hasOwnProperty.call(style, key)) {
        const element = style[key];
        if (element) {
          root = root.concat('--' + key, ': ', element, ';');
        }
      }
    }
    root = root.concat('}');
    styleTag.innerHTML = root;
    headTag.appendChild(styleTag);
  }
}

export class Environment {
  public static getClientId(): string {
    const storageKey = 'clientid';
    let storedIdentifier = localStorage.getItem(storageKey);
    if (!storedIdentifier) {
      storedIdentifier = GUID.newGUID();
      localStorage.setItem(storageKey, storedIdentifier);
    }
    return storedIdentifier;
  }
}

export class PromiseHelper {
  public static timeout<T>(promise: Promise<T>, milliseconds: number): Promise<T | never> {
    let timer;
    return Promise.race([promise, new Promise<never>((_r, rej) => (timer = setTimeout(_r, milliseconds)))]).finally(
      () => clearTimeout(timer),
    );
  }

  public static delay<T>(action: () => Promise<T>, milliseconds: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      setTimeout(() => {
        action()
          .then((_) => resolve(_))
          .catch((err) => reject(err));
      }, milliseconds);
    });
  }
}

export class DomHelper {
  public static getMaxZIndex(): number {
    return Math.max(
      ...Array.from(document.querySelectorAll('body *'), (el) => parseFloat(window.getComputedStyle(el).zIndex)).filter(
        (zIndex) => !Number.isNaN(zIndex),
      ),
      0,
    );
  }
}

export class FileHelper {
  /**
   * Downloads a PPTX file from a base64-encoded string.
   * @param base64Data - The base64 string of the PPTX file.
   * @param fileName - The name of the file to be downloaded.
   * @returns Promise<void>
   */
  public static async downloadPptxFromBase64(base64Data: string, fileName: string): Promise<void> {
    try {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      throw new Error(error);
    }
  }
  public static getExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === fileName.length - 1) {
      return null; //
    }
    return fileName.slice(lastDot + 1).toLowerCase();
  }
}

export class UrlHelper {
  public static buildUrl(base: string, ...path: string[]): string {
    let url = base ?? '';
    if (path) {
      for (const p of path) {
        let rawp = p ?? '';
        while (rawp.startsWith('/')) {
          rawp = rawp.substring(1);
        }
        if (url.endsWith('/')) {
          url += rawp;
        } else {
          url += '/' + rawp;
        }
      }
    }
    return url;
  }
}
